import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarDataHora } from '@/pages/loja/utils';
import type { EntregaRecente } from '@/pages/loja/types';

interface Props {
  pontoId: number;
  aoFechar: () => void;
}

export default function ModalEntregas({ pontoId, aoFechar }: Props) {
  const [entregas, setEntregas] = useState<EntregaRecente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<number | null>(null);
  const [desfazendo, setDesfazendo] = useState(false);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('loja_entregas_recentes', {
        p_ponto: pontoId,
        p_limite: 20,
      });
      if (error) throw error;
      setEntregas((data as EntregaRecente[]) || []);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível carregar as entregas.'));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pontoId]);

  async function desfazer(entrega: EntregaRecente) {
    const retiradaId = entrega.retirada_id ?? entrega.id;
    if (retiradaId == null) return;
    setDesfazendo(true);
    try {
      const { error } = await supabase.rpc('loja_desfazer_retirada', {
        p_retirada: retiradaId,
        p_motivo: 'Desfeito pelo operador',
      });
      if (error) throw error;
      setConfirmando(null);
      carregar();
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos desfazer a entrega.'));
    } finally {
      setDesfazendo(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={aoFechar}
    >
      <div
        className="max-h-[85vh] w-full max-w-[480px] overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h3 className="text-base font-semibold text-foreground-950">Últimas entregas</h3>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-5">
          {erro && (
            <p className="mb-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
              {erro}
            </p>
          )}

          {carregando ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-secondary-100" />
              ))}
            </div>
          ) : entregas.length === 0 ? (
            <p className="py-8 text-center text-sm text-foreground-600">
              Nenhuma entrega recente.
            </p>
          ) : (
            <div className="space-y-3">
              {entregas.map((entrega) => {
                const retiradaId = entrega.retirada_id ?? entrega.id;
                const itensTexto = (entrega.itens || [])
                  .map((i) => `${i.quantidade}× ${i.nome}`)
                  .join(', ');
                return (
                  <div
                    key={retiradaId ?? itensTexto}
                    className="rounded-xl border border-secondary-200 bg-background-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-950">
                          {entrega.cliente || 'Cliente'}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-600">{itensTexto}</p>
                        <p className="mt-1 text-xs text-foreground-500">
                          {formatarDataHora(entrega.quando)}
                        </p>
                      </div>
                      {retiradaId != null && (
                        <button
                          type="button"
                          onClick={() =>
                            setConfirmando(confirmando === retiradaId ? null : retiradaId)
                          }
                          className="shrink-0 cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          desfazer
                        </button>
                      )}
                    </div>

                    {confirmando === retiradaId && (
                      <div className="mt-3 rounded-lg bg-amber-50 p-3">
                        <p className="text-xs text-foreground-700">Desfazer esta entrega?</p>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() => desfazer(entrega)}
                            disabled={desfazendo}
                            className="flex-1 cursor-pointer whitespace-nowrap rounded-lg bg-danger-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-danger-700 disabled:opacity-60"
                          >
                            {desfazendo ? 'Desfazendo…' : 'Sim, desfazer'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmando(null)}
                            className="flex-1 cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-2 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}